import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Clock, XCircle, CheckCircle2, FileSearch, ShieldAlert } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { RenderedHtml, toPlainText } from 'modules/editor';
import * as ResultModule from 'modules/result';
import {
    AnswerReviewBody,
    AnswerReviewStyles,
    hasAnswer,
    type AnswerReviewItem,
} from 'components/result/AnswerReviewBody';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';

type Respondent = ResultModule.Types.IEntity.Result;
type DetailAnswer = ResultModule.Types.IEntity.ResultDetails['answers'][number];

// Backend caps `perPage` at 100 (ListResultsDto @Max); 100 covers the selector
// for virtually all tests. A deep-linked attempt beyond the first 100 still
// loads its detail via useSingle — it just won't be highlighted in the dropdown.
const RESPONDENT_PAGE_SIZE = 100;

// Proctoring/anti-cheat event types (apps/api AttemptEvent.type) → i18n label keys.
// Unknown types fall back to their raw type string so nothing is silently hidden.
const EVENT_LABEL_KEYS: Record<string, string> = {
    TAB_SWITCH: 'event_tab_switch',
    FOCUS_LOST: 'event_focus_lost',
    COPY_PASTE: 'event_copy_paste',
    SCREENSHOT: 'event_screenshot',
    FULLSCREEN_EXIT: 'event_fullscreen_exit',
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Seconds → HH:MM:SS for the timer denominator/total. */
const fmtClock = (totalSeconds: number): string => {
    const s = Math.max(0, Math.floor(totalSeconds));
    return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
};

/** Seconds → m:ss' for per-question time (only rendered when > 0). */
const fmtQuestionTime = (seconds: number): string =>
    `${pad2(Math.floor(seconds / 60))}:${pad2(seconds % 60)}'`;

const fmtTimeOfDay = (d: Date | null): string =>
    d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '—';

const fmtDay = (d: Date | null): string => {
    if (!d) return '—';
    const date = new Date(d);
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const stripHtml = toPlainText;

const truncate = (text: string, max = 64): string =>
    text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

export const SheetsReviewPage: React.FC = () => {
    const { t } = useTranslation();
    const { testId } = useParams<{ testId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const attemptId = searchParams.get('attemptId') ?? '';

    const { items, isLoading: listLoading, isError: listError } = ResultModule.Hooks.useList({
        params: { testId: testId || undefined, perPage: RESPONDENT_PAGE_SIZE },
        enabled: !!testId,
    });

    // Newest end-date first, matching the Results table default ordering.
    const ordered = useMemo<Respondent[]>(
        () =>
            [...items].sort(
                (a, b) => (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0),
            ),
        [items],
    );

    // Disambiguate retakes (same name → append the end date) for the selector.
    const nameCounts = useMemo(() => {
        const counts = new Map<string, number>();
        ordered.forEach((r) => counts.set(r.userName, (counts.get(r.userName) ?? 0) + 1));
        return counts;
    }, [ordered]);

    const labelFor = (r: Respondent): string =>
        (nameCounts.get(r.userName) ?? 0) > 1
            ? `${r.userName} · ${fmtDay(r.completedAt)}`
            : r.userName;

    const selectedIndex = ordered.findIndex((r) => r.id === attemptId);

    // No explicit selection → land on the first respondent (reflected in the URL).
    useEffect(() => {
        if (listLoading) return;
        if (!attemptId && ordered.length > 0) {
            setSearchParams({ attemptId: ordered[0].id }, { replace: true });
        }
    }, [listLoading, attemptId, ordered, setSearchParams]);

    const selectAttempt = (id: string) => setSearchParams({ attemptId: id });

    const { item: detail, isLoading: detailLoading } = ResultModule.Hooks.useSingle({
        id: attemptId,
        enabled: !!attemptId,
    });

    const [openIds, setOpenIds] = useState<Set<string>>(new Set());
    const [eventsOpen, setEventsOpen] = useState(false);
    // Reset the accordions when switching respondents.
    useEffect(() => {
        setOpenIds(new Set());
        setEventsOpen(false);
    }, [attemptId]);

    const answers = (detail?.answers ?? []) as DetailAnswer[];
    const events = detail?.proctoringEvents ?? [];
    const allOpen = answers.length > 0 && openIds.size === answers.length;

    const toggleAll = () =>
        setOpenIds(allOpen ? new Set() : new Set(answers.map((a) => a.questionId)));

    const toggleOne = (qid: string) =>
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(qid)) next.delete(qid);
            else next.add(qid);
            return next;
        });

    if (listLoading) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
            </div>
        );
    }

    if (listError || ordered.length === 0) {
        return (
            <div className="space-y-6">
                <h2 className="text-xl font-semibold text-foreground">{t('test_sheets_review')}</h2>
                <div className="sr-empty">
                    <FileSearch className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                        {listError ? t('failed_to_load_results') : t('no_completed_attempts')}
                    </p>
                </div>
            </div>
        );
    }

    // Only show the review once the loaded detail matches the selected attempt —
    // avoids a flash of empty/stale data before the fetch (or auto-select) lands.
    const showReview = !!attemptId && !detailLoading && detail.id === attemptId;
    const percentage = detail.percentage ?? 0;
    const passed = detail.passed;
    const duration = detail.duration ?? 0;
    const timeLimit = detail.timeLimit ?? null;

    return (
        <div className="ts ts-shell sr-root">
            {/* Header: title + respondent selector + prev/next */}
            <div className="sr-header">
                <h2 className="sr-title">{t('test_sheets_review')}</h2>
                <div className="sr-respondent-picker">
                    <span className="sr-respondent-label">{t('respondent')}</span>
                    <Select value={attemptId} onValueChange={selectAttempt}>
                        <SelectTrigger className="w-56">
                            <SelectValue placeholder={t('select_respondent')} />
                        </SelectTrigger>
                        <SelectContent>
                            {ordered.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                    {labelFor(r)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="sr-nav-arrows">
                        <button
                            type="button"
                            className="sr-arrow"
                            aria-label={t('previous_respondent')}
                            disabled={selectedIndex <= 0}
                            onClick={() => selectAttempt(ordered[selectedIndex - 1].id)}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            type="button"
                            className="sr-arrow"
                            aria-label={t('next_respondent')}
                            disabled={selectedIndex < 0 || selectedIndex >= ordered.length - 1}
                            onClick={() => selectAttempt(ordered[selectedIndex + 1].id)}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {!showReview ? (
                <div className="flex items-center justify-center min-h-[240px]">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
                </div>
            ) : (
                <>
                    {/* Respondent identity */}
                    <div className="sr-card sr-identity">
                        <span className="sr-eyebrow">{t('respondent')}</span>
                        <span className="sr-identity-name">{detail.user?.name || '—'}</span>
                    </div>

                    {/* Result + Timer */}
                    <div className="sr-summary-grid">
                        <div className="sr-card sr-result">
                            <span className="sr-eyebrow">{t('result')}</span>
                            <div className="sr-result-row">
                                {passed != null && <div
                                    className={cn(
                                        'sr-result-verdict',
                                        passed ? 'sr-pass' : 'sr-fail',
                                    )}
                                >
                                    {passed ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                                    <span>{passed ? t('test_passed') : t('test_failed')}</span>
                                </div>}
                                <ResultDonut percentage={percentage} passed={passed} />
                            </div>
                            <span className="sr-result-points">
                                {detail.score}/{detail.maxScore} {t('pts')}
                            </span>
                        </div>

                        <div className="sr-card sr-timer">
                            <span className="sr-eyebrow">{t('timer')}</span>
                            <div className="sr-timer-total">
                                <Clock size={18} />
                                <span className="sr-timer-clock">{fmtClock(duration)}</span>
                                {timeLimit != null && (
                                    <span className="sr-timer-limit">/ {fmtClock(timeLimit)}</span>
                                )}
                            </div>
                            <div className="sr-timer-meta">
                                <div>
                                    <span className="sr-timer-meta-label">{t('start_time')}</span>
                                    <span className="sr-timer-meta-value">
                                        {fmtTimeOfDay(detail.startedAt)}
                                    </span>
                                </div>
                                <div>
                                    <span className="sr-timer-meta-label">{t('end_time')}</span>
                                    <span className="sr-timer-meta-value">
                                        {fmtTimeOfDay(detail.completedAt)}
                                    </span>
                                </div>
                                <div>
                                    <span className="sr-timer-meta-label">{t('date')}</span>
                                    <span className="sr-timer-meta-value">
                                        {fmtDay(detail.completedAt ?? detail.startedAt)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Integrity / proctoring events — collapsible minimal timeline */}
                    <div className="sr-card sr-events">
                        <button
                            type="button"
                            className="sr-events-head"
                            aria-expanded={eventsOpen}
                            onClick={() => setEventsOpen((v) => !v)}
                        >
                            <span className="sr-eyebrow sr-events-title">
                                <ShieldAlert size={14} />
                                {t('integrity_events')} ({events.length})
                            </span>
                            <ChevronDown
                                size={16}
                                className="sr-events-chevron"
                                style={{ transform: eventsOpen ? 'rotate(180deg)' : 'none' }}
                            />
                        </button>
                        {eventsOpen &&
                            (events.length === 0 ? (
                                <p className="sr-events-empty">{t('no_integrity_events')}</p>
                            ) : (
                                <ol className="sr-timeline">
                                    {events.map((ev, i) => {
                                        const labelKey = EVENT_LABEL_KEYS[ev.type];
                                        const showDesc =
                                            !!ev.description && ev.description !== ev.type;
                                        return (
                                            <li key={`${ev.type}-${i}`} className="sr-tl-item">
                                                <span
                                                    className={cn('sr-tl-dot', `sr-sev-${ev.severity}`)}
                                                    title={t(`severity_${ev.severity}`)}
                                                />
                                                <time className="sr-tl-time">
                                                    {fmtTimeOfDay(ev.timestamp)}
                                                </time>
                                                <span className="sr-tl-type">
                                                    {labelKey ? t(labelKey) : ev.type}
                                                </span>
                                                {showDesc && (
                                                    <span className="sr-tl-desc">{ev.description}</span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ol>
                            ))}
                    </div>

                    {/* Questions accordion */}
                    <div className="sr-card sr-questions">
                        <div className="sr-questions-head">
                            <span className="sr-eyebrow">
                                {t('questions')} ({answers.length})
                            </span>
                            <button type="button" className="sr-expand-toggle" onClick={toggleAll}>
                                {allOpen ? t('collapse_all') : t('expand_all')}
                                <ChevronDown
                                    size={14}
                                    style={{ transform: allOpen ? 'rotate(180deg)' : 'none' }}
                                />
                            </button>
                        </div>

                        {answers.map((a, idx) => {
                            const open = openIds.has(a.questionId);
                            const answered = hasAnswer(a.answer);
                            const badgeClass = !answered
                                ? 'sr-pts-skipped'
                                : a.isCorrect
                                  ? 'sr-pts-correct'
                                  : 'sr-pts-incorrect';
                            return (
                                <div key={a.questionId} className="sr-q">
                                    <button
                                        type="button"
                                        className="sr-q-row"
                                        aria-expanded={open}
                                        onClick={() => toggleOne(a.questionId)}
                                    >
                                        <span className="sr-q-num">Q. {idx + 1}</span>
                                        <span className="sr-q-text">
                                            {truncate(stripHtml(a.questionText)) || t('question')}
                                        </span>
                                        {a.timeSpent > 0 && (
                                            <span className="sr-q-time">
                                                {fmtQuestionTime(a.timeSpent)}
                                            </span>
                                        )}
                                        <span className={cn('sr-q-pts', badgeClass)}>
                                            {a.pointsEarned}/{a.maxPoints}P
                                        </span>
                                        <ChevronDown
                                            size={16}
                                            className="sr-q-chevron"
                                            style={{ transform: open ? 'rotate(180deg)' : 'none' }}
                                        />
                                    </button>
                                    {open && (
                                        <div className="sr-q-body">
                                            <div className="sr-q-prompt">
                                                {a.questionText ? (
                                                    <RenderedHtml html={a.questionText} />
                                                ) : (
                                                    t('question')
                                                )}
                                            </div>
                                            <AnswerReviewBody answer={a as AnswerReviewItem} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <AnswerReviewStyles />
            <SheetsReviewStyles />
        </div>
    );
};

const ResultDonut: React.FC<{ percentage: number; passed: boolean | null }> = ({ percentage, passed }) => {
    const radius = 34;
    const circumference = 2 * Math.PI * radius;
    const clamped = Math.max(0, Math.min(100, percentage));
    const offset = circumference - (clamped / 100) * circumference;
    return (
        <div className="sr-donut">
            <svg viewBox="0 0 80 80" width="80" height="80">
                <circle cx="40" cy="40" r={radius} className="sr-donut-track" />
                <circle
                    cx="40"
                    cy="40"
                    r={radius}
                    className={cn('sr-donut-fill', passed == null ? 'stroke-primary-600' : passed ? 'sr-donut-pass' : 'sr-donut-fail')}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform="rotate(-90 40 40)"
                />
            </svg>
            <span className="sr-donut-label">{clamped}%</span>
        </div>
    );
};

const SheetsReviewStyles: React.FC = () => (
    <style>{`
.ts-shell.sr-root {
    background: transparent;
    display: flex; flex-direction: column; gap: 16px;
}
.sr-card {
    background: var(--ts-card-bg);
    border: 1px solid var(--ts-gray-200);
    border-radius: 12px;
    padding: 18px 20px;
}
.sr-eyebrow {
    font-size: 11px; font-weight: 700;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--ts-gray-500);
}
.sr-header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px; flex-wrap: wrap;
}
.sr-title { font-size: 20px; font-weight: 700; color: var(--ts-gray-900); margin: 0; }
.sr-respondent-picker { display: flex; align-items: center; gap: 10px; }
.sr-respondent-label {
    font-size: 12px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.04em; color: var(--ts-gray-500);
}
.sr-nav-arrows { display: flex; gap: 6px; }
.sr-arrow {
    width: 34px; height: 34px; border-radius: 8px;
    display: inline-flex; align-items: center; justify-content: center;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
    color: var(--ts-gray-700); cursor: pointer; transition: background .15s ease;
}
.sr-arrow:hover:not(:disabled) { background: var(--ts-gray-100); }
.sr-arrow:disabled { opacity: 0.4; cursor: not-allowed; }

.sr-identity { display: flex; flex-direction: column; gap: 6px; }
.sr-identity-name { font-size: 18px; font-weight: 700; color: var(--ts-gray-900); }

.sr-summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 768px) {
    .sr-summary-grid { grid-template-columns: 1fr; }
    .sr-respondent-picker { flex-wrap: wrap; }
}

.sr-result { display: flex; flex-direction: column; gap: 10px; }
.sr-result-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.sr-result-verdict { display: inline-flex; align-items: center; gap: 8px; font-size: 18px; font-weight: 700; }
.sr-result-verdict.sr-pass { color: var(--ts-success-strong); }
.sr-result-verdict.sr-fail { color: var(--ts-danger-strong); }
.sr-result-points { font-size: 13px; font-weight: 600; color: var(--ts-gray-600); }

.sr-donut { position: relative; width: 80px; height: 80px; flex-shrink: 0; }
.sr-donut-track { fill: none; stroke: var(--ts-gray-200); stroke-width: 8; }
.sr-donut-fill { fill: none; stroke-width: 8; stroke-linecap: round; transition: stroke-dashoffset .4s ease; }
.sr-donut-pass { stroke: var(--ts-success-strong); }
.sr-donut-fail { stroke: var(--ts-danger-strong); }
.sr-donut-label {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; font-weight: 700; color: var(--ts-gray-900);
    font-variant-numeric: tabular-nums;
}

.sr-timer { display: flex; flex-direction: column; gap: 12px; }
.sr-timer-total { display: flex; align-items: center; gap: 8px; color: var(--ts-gray-900); }
.sr-timer-clock { font-size: 22px; font-weight: 700; font-variant-numeric: tabular-nums; }
.sr-timer-limit { font-size: 16px; color: var(--ts-gray-500); font-variant-numeric: tabular-nums; }
.sr-timer-meta { display: flex; gap: 24px; flex-wrap: wrap; }
.sr-timer-meta > div { display: flex; flex-direction: column; gap: 2px; }
.sr-timer-meta-label {
    font-size: 11px; color: var(--ts-gray-500); font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.04em;
}
.sr-timer-meta-value { font-size: 14px; font-weight: 600; color: var(--ts-gray-900); font-variant-numeric: tabular-nums; }

.sr-questions { padding: 0; }
.sr-questions-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px; border-bottom: 1px solid var(--ts-gray-100);
}
.sr-expand-toggle {
    display: inline-flex; align-items: center; gap: 6px;
    background: transparent; border: 0; cursor: pointer;
    font-family: inherit; font-size: 13px; font-weight: 600; color: var(--ts-primary);
}
.sr-expand-toggle svg { transition: transform .2s ease; }

.sr-q + .sr-q { border-top: 1px solid var(--ts-gray-100); }
.sr-q-row {
    display: flex; align-items: center; gap: 14px;
    width: 100%; padding: 14px 20px;
    background: transparent; border: 0; cursor: pointer;
    font-family: inherit; text-align: left;
}
.sr-q-row:hover { background: var(--ts-gray-100); }
.sr-q-num { font-size: 13px; font-weight: 700; color: var(--ts-gray-700); flex-shrink: 0; min-width: 36px; }
.sr-q-text {
    flex: 1; min-width: 0;
    font-size: 14px; color: var(--ts-gray-700);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sr-q-time {
    font-size: 12px; color: var(--ts-gray-500);
    font-variant-numeric: tabular-nums; flex-shrink: 0;
}
.sr-q-pts {
    font-size: 12px; font-weight: 700;
    padding: 3px 8px; border-radius: 999px; flex-shrink: 0;
    font-variant-numeric: tabular-nums;
}
.sr-pts-correct { background: var(--ts-success-bg); color: var(--ts-success-text); }
.sr-pts-incorrect { background: var(--ts-danger-bg); color: var(--ts-danger-text); }
.sr-pts-skipped { background: var(--ts-gray-100); color: var(--ts-gray-500); }
.sr-q-chevron { color: var(--ts-gray-400); transition: transform .2s ease; flex-shrink: 0; }
.sr-q-body { padding: 4px 20px 22px; }
.sr-q-prompt {
    font-size: 16px; font-weight: 600; line-height: 1.5;
    color: var(--ts-gray-900); margin-bottom: 16px;
}

.sr-empty {
    display: flex; flex-direction: column; align-items: center; text-align: center;
    padding: 56px 24px; border: 1px solid var(--ts-gray-200);
    border-radius: 12px; background: var(--ts-card-bg);
}

/* Integrity events — collapsible accordion + minimal timeline */
.sr-events { padding: 0; }
.sr-events-head {
    display: flex; align-items: center; justify-content: space-between;
    width: 100%; padding: 16px 20px;
    background: transparent; border: 0; cursor: pointer;
    font-family: inherit; text-align: left;
}
.sr-events-head:hover { background: var(--ts-gray-100); border-radius: 12px; }
.sr-events-title { display: inline-flex; align-items: center; gap: 6px; }
.sr-events-chevron { color: var(--ts-gray-400); transition: transform .2s ease; flex-shrink: 0; }
.sr-events-empty { margin: 0; padding: 0 20px 18px; font-size: 13px; color: var(--ts-gray-500); }

.sr-timeline { list-style: none; margin: 0; padding: 2px 20px 16px; }
.sr-tl-item {
    position: relative; display: flex; align-items: baseline; gap: 12px;
    padding: 7px 0 7px 18px;
}
.sr-tl-item:not(:last-child)::before {
    content: ''; position: absolute; left: 3px; top: 14px; bottom: -2px;
    width: 1px; background: var(--ts-gray-200);
}
.sr-tl-dot {
    position: absolute; left: 0; top: 9px;
    width: 7px; height: 7px; border-radius: 999px;
    box-shadow: 0 0 0 2px var(--ts-card-bg);
}
.sr-sev-low { background: var(--ts-gray-400); }
.sr-sev-medium { background: #d97706; }
.sr-sev-high { background: var(--ts-danger-strong); }
.sr-tl-time {
    font-size: 12px; color: var(--ts-gray-500);
    font-variant-numeric: tabular-nums; flex-shrink: 0; min-width: 44px;
}
.sr-tl-type { font-size: 14px; font-weight: 600; color: var(--ts-gray-900); flex-shrink: 0; }
.sr-tl-desc {
    font-size: 13px; color: var(--ts-gray-500);
    min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
`}</style>
);

export default SheetsReviewPage;
